import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  CircularProgress,
  Container,
  Card,
  CardContent,
  Button,
  Chip
} from '@mui/material';
import DashboardLayout from '../../layouts/DashboardLayout';
import { approveProductRequest, rejectProductRequest, getProductRequestList } from '../../api/productRequests/index.ts';
import { useSnack } from '../../services/SnakbarProvider';
import { handleError } from '../../services/errorHandler';
import { useCompany } from '../../contexts/CompanyContext';
import dayjs from 'dayjs';

interface ProductRequest {
  id: number;
  product: {
    code: string;
    description?: string;
  };
  quantity: number;
  reservedDate: string;
  reserveDuration: number;
  solicitor: string;
  status: {
    code: string;
    name: string;
  };
  createdAt: string;
}

const ProductRequestApprovalScreen = () => {
  const [productRequests, setProductRequests] = useState<ProductRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { openSnack } = useSnack();
  const { selectedCompany } = useCompany();

  const fetchProductRequests = async () => {
    try {
      setLoading(true);
      // Get status ID for REQ_CREATED - we'll need to filter by status code
      // For now, we'll fetch all and filter client-side, or we can add statusCode filter to API
      const response = await getProductRequestList({
        companyId: selectedCompany?.id,
      });
      
      // Filter for REQ_CREATED status
      const pendingRequests = response.data.filter((pr: ProductRequest) => 
        pr.status?.code === 'REQ_CREATED'
      );
      
      setProductRequests(pendingRequests);
    } catch (error) {
      openSnack(handleError(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await approveProductRequest(id);
      fetchProductRequests();
      openSnack('Solicitud de producto aprobada', 'success');
    } catch (error) {
      openSnack(handleError(error), 'error');
    }
  };

  const handleReject = async (id: number) => {
    try {
      await rejectProductRequest(id);
      fetchProductRequests();
      openSnack('Solicitud de producto rechazada', 'success');
    } catch (error) {
      openSnack(handleError(error), 'error');
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchProductRequests();
    }
  }, [selectedCompany]);

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box py={3}>
          <Typography variant="h4" gutterBottom>
            Aprobaciones de Solicitudes de Producto
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : productRequests.length === 0 ? (
            <Box py={4}>
              <Typography variant="h6" color="text.secondary" align="center">
                No hay solicitudes de producto pendientes de aprobación
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {productRequests.map((pr) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={pr.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {pr.product.code}
                      </Typography>
                      {pr.product.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {pr.product.description}
                        </Typography>
                      )}
                      <Box mt={2}>
                        <Typography variant="body2">
                          <strong>Cantidad:</strong> {pr.quantity}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Fecha de Reserva:</strong> {dayjs(pr.reservedDate).format('YYYY-MM-DD')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Duración:</strong> {pr.reserveDuration} días
                        </Typography>
                        <Typography variant="body2">
                          <strong>Solicitante:</strong> {pr.solicitor}
                        </Typography>
                        <Typography variant="body2" mt={1}>
                          <strong>Fecha de Creación:</strong> {dayjs(pr.createdAt).format('YYYY-MM-DD')}
                        </Typography>
                      </Box>
                      <Box mt={2} display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApprove(pr.id)}
                          fullWidth
                        >
                          Aprobar
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleReject(pr.id)}
                          fullWidth
                        >
                          Rechazar
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default ProductRequestApprovalScreen;

